export const slice = [].slice;
export const splice = [].splice;
export const push = [].push;
export const toString = Object.prototype.toString;
export const getPrototypeOf = Object.getPrototypeOf;

/**
 * @kind function
 *
 * @description
 * Determines if a reference is an `Array`.
 *
 * @param {*} value Reference to check.
 * @returns {boolean} True if `value` is an `Array`.
 */
export const isArray = function isArray(arr) {
    return Array.isArray(arr) || arr instanceof Array;
}

/**
 * Creates a new object without a prototype. This object is useful for lookup without having to
 * guard against prototypically inherited properties via hasOwnProperty.
 *
 * Related micro-benchmarks:
 * - http://jsperf.com/object-create2
 * - http://jsperf.com/proto-map-lookup/2
 * - http://jsperf.com/for-in-vs-object-keys2
 *
 * @returns {Object}
 */
function createMap() {
  return Object.create(null);
}

/**
 * @private
 * @param {*} obj
 * @return {boolean} Returns true if `obj` is an array or array-like object (NodeList, Arguments,
 *                   String ...)
 */
export const isArrayLike = function isArrayLike(obj) {

    // `null`, `undefined` and `window` are not array-like
    if (obj == null || isWindow(obj)) return false;

    // arrays, strings and jQuery/jqLite objects are array like
    // * jqLite is either the jQuery or jqLite constructor function
    // * we have to check the existence of jqLite first as this method is called
    //   via the forEach method when constructing the jqLite object in the first place
    if (isArray(obj) || isString(obj)) return true;

    // Support: iOS 8.2 (not reproducible in simulator)
    // "length" in obj used to prevent JIT error (gh-11508)
    var length = 'length' in Object(obj) && obj.length;

    // NodeList objects (with `item` method) and
    // other objects with suitable length characteristics are array-like
    return isNumber(length) && (length >= 0 && (((length - 1) in obj) || (typeof obj.item === 'function')));

}

/**
 * @description
 * Determines if a reference is an `Object`. Unlike `typeof` in JavaScript, `null`s are not
 * considered to be objects. Note that JavaScript arrays are objects.
 *
 * @param {*} value Reference to check.
 * @returns {boolean} True if `value` is an `Object` but not `null`.
 */
export const isObject = (value) => {
    // http://jsperf.com/isobject4
    return value !== null && typeof value === 'object';
};

/**
 * Determine if a value is an object with a null prototype
 *
 * @returns {boolean} True if `value` is an `Object` with a null prototype
 */
export const isBlankObject = (value) => {
    return value !== null && typeof value === 'object' && !getPrototypeOf(value);
};

/**
 * Checks if `obj` is a window object.
 *
 * @private
 * @param {*} obj Object to check
 * @returns {boolean} True if `obj` is a window obj.
 */
export const isWindow =
    function isWindow(obj) {
        return obj && obj.window === obj;
    }


/**
 * @description
 * Determines if a reference is undefined.
 *
 * @param {*} value Reference to check.
 * @returns {boolean} True if `value` is undefined.
 */
export const isUndefined = function isUndefined(value) { return typeof value === 'undefined'; }

/**
 * @description
 * Determines if a reference is defined.
 *
 * @param {*} value Reference to check.
 * @returns {boolean} True if `value` is defined.
 */
function isDefined(value) { return typeof value !== 'undefined'; }


/**
 * @description
 * Determines if two objects or two values are equivalent. Supports value types, regular
 * expressions, arrays and objects.
 *
 * Two objects or values are considered equivalent if at least one of the following is true:
 *
 * * Both objects or values pass `===` comparison.
 * * Both objects or values are of the same type and all of their properties are equal by
 *   comparing them with `angular.equals`.
 * * Both values are NaN. (In JavaScript, NaN == NaN => false. But we consider two NaN as equal)
 * * Both values represent the same regular expression (In JavaScript,
 *   /abc/ == /abc/ => false. But we consider two regular expressions as equal when their textual
 *   representation matches).
 *
 * During a property comparison, properties of `function` type and properties with names
 * that begin with `$` are ignored.
 *
 * Scope and DOMWindow objects are being compared only by identify (`===`).
 *
 * @param {*} o1 Object or value to compare.
 * @param {*} o2 Object or value to compare.
 * @returns {boolean} True if arguments are equal.
 *
 *

 */
export const equals = function equals(o1, o2) {
    if (o1 === o2) return true;
    if (o1 === null || o2 === null) return false;
    // eslint-disable-next-line no-self-compare
    if (o1 !== o1 && o2 !== o2) return true; // NaN === NaN
    var t1 = typeof o1,
        t2 = typeof o2,
        length, key, keySet;
    if (t1 === t2 && t1 === 'object') {
        if (isArray(o1)) {
            if (!isArray(o2)) return false;
            if ((length = o1.length) === o2.length) {
                for (key = 0; key < length; key++) {
                    if (!equals(o1[key], o2[key])) return false;
                }
                return true;
            }
        } else if (isDate(o1)) {
            if (!isDate(o2)) return false;
            return simpleCompare(o1.getTime(), o2.getTime());
        } else if (isRegExp(o1)) {
            if (!isRegExp(o2)) return false;
            return o1.toString() === o2.toString();
        } else {
            if (isWindow(o1) || isWindow(o2) ||
                isArray(o2) || isDate(o2) || isRegExp(o2)) return false;
            keySet = createMap();
            for (key in o1) {
                if (key.charAt(0) === '$' || isFunction(o1[key])) continue;
                if (!equals(o1[key], o2[key])) return false;
                keySet[key] = true;
            }
            for (key in o2) {
                if (!(key in keySet) &&
                    key.charAt(0) !== '$' &&
                    isDefined(o2[key]) &&
                    !isFunction(o2[key])) return false;
            }
            return true;
        }
    }
    return false;
};

/**
 * @kind function
 *
 * @description
 * Invokes the `iterator` function once for each item in `obj` collection, which can be either an
 * object or an array. The `iterator` function is invoked with `iterator(value, key, obj)`, where `value`
 * is the value of an object property or an array element, `key` is the object property key or
 * array element index and obj is the `obj` itself. Specifying a `context` for the function is optional.
 *
 * It is worth noting that `.forEach` does not iterate over inherited properties because it filters
 * using the `hasOwnProperty` method.
 *
 * Unlike ES262's
 * [Array.prototype.forEach](http://www.ecma-international.org/ecma-262/5.1/#sec-15.4.4.18),
 * providing 'undefined' or 'null' values for `obj` will not throw a TypeError, but rather just
 * return the value provided.
 *
   ```js
     var values = {name: 'misko', gender: 'male'};
     var log = [];
     angular.forEach(values, function(value, key) {
       this.push(key + ': ' + value);
     }, log);
     expect(log).toEqual(['name: misko', 'gender: male']);
   ```
 *
 * @param {Object|Array} obj Object to iterate over.
 * @param {Function} iterator Iterator function.
 * @param {Object=} context Object to become context (`this`) for the iterator function.
 * @returns {Object|Array} Reference to `obj`.
 */

export const forEach = function forEach(obj, iterator, context) {
  var key, length;
  if (obj) {
    if (isFunction(obj)) {
      for (key in obj) {
        if (key !== 'prototype' && key !== 'length' && key !== 'name' && obj.hasOwnProperty(key)) {
          iterator.call(context, obj[key], key, obj);
        }
      }
    } else if (isArray(obj) || isArrayLike(obj)) {
      var isPrimitive = typeof obj !== 'object';
      for (key = 0, length = obj.length; key < length; key++) {
        if (isPrimitive || key in obj) {
          iterator.call(context, obj[key], key, obj);
        }
      }
    } else if (obj.forEach && obj.forEach !== forEach) {
        obj.forEach(iterator, context, obj);
    } else if (isBlankObject(obj)) {
      // createMap() fast path --- Safe to avoid hasOwnProperty check because prototype chain is empty
      for (key in obj) {
        iterator.call(context, obj[key], key, obj);
      }
    } else if (typeof obj.hasOwnProperty === 'function') {
      // Slow path for objects inheriting Object.prototype, hasOwnProperty check needed
      for (key in obj) {
        if (obj.hasOwnProperty(key)) {
          iterator.call(context, obj[key], key, obj);
        }
      }
    } else {
      // Slow path for objects which do not have a method `hasOwnProperty`
      for (key in obj) {
        if (hasOwnProperty.call(obj, key)) {
          iterator.call(context, obj[key], key, obj);
        }
      }
    }
  }
  return obj;
}


export const hasCustomToString = function hasCustomToString(obj) {
    return isFunction(obj.toString) && obj.toString !== toString;
};

/**@kind function
 *
 * @description
 * A function that returns its first argument. This function is useful when writing code in the
 * functional style.
 *
   ```js
   function transformer(transformationFn, value) {
     return (transformationFn || angular.identity)(value);
   };

   // E.g.
   function getResult(fn, input) {
     return (fn || angular.identity)(input);
   };

   getResult(function(n) { return n * 2; }, 21);   // returns 42
   getResult(null, 21);                            // returns 21
   getResult(undefined, 21);                       // returns 21
   ```
 *
 * @param {*} value to be returned.
 * @returns {*} the value passed in.
 */

export const valueFn = function valueFn(value) { return function valueRef() { return value; }; }



var ALL_COLONS = /:/g;
export const timezoneToOffset = function timezoneToOffset(timezone, fallback) {
    // Support: IE 9-11 only, Edge 13-15+
    // IE/Edge do not "understand" colon (`:`) in timezone
    timezone = timezone.replace(ALL_COLONS, '');
    var requestedTimezoneOffset = Date.parse('Jan 01, 1970 00:00:00 ' + timezone) / 60000;
    return isNumberNaN(requestedTimezoneOffset) ? fallback : requestedTimezoneOffset;
};


export const addDateMinutes = function addDateMinutes(date, minutes) {
    date = new Date(date.getTime());
    date.setMinutes(date.getMinutes() + minutes);
    return date;
};


export const convertTimezoneToLocal = function convertTimezoneToLocal(date, timezone, reverse) {
    reverse = reverse ? -1 : 1;
    var dateTimezoneOffset = date.getTimezoneOffset();
    var timezoneOffset = timezoneToOffset(timezone, dateTimezoneOffset);
    return addDateMinutes(date, reverse * (timezoneOffset - dateTimezoneOffset));
};


/**
 * Tries to decode the URI component without throwing an exception.
 *
 * @private
 * @param str value potential URI component to check.
 * @returns {boolean} True if `value` can be decoded
 * with the decodeURIComponent function.
 */
function tryDecodeURIComponent(value) {
    try {
        return decodeURIComponent(value);
    } catch (e) {
        // Ignore any invalid uri component.
    }
}


/**
 * Parses an escaped url query string into key-value pairs.
 * @returns {Object.<string,boolean|Array>}
 */
export const parseKeyValue = function parseKeyValue( /**string*/ keyValue) {
    var obj = {};
    forEach((keyValue || '').split('&'), function(keyValue) {
        var splitPoint, key, val;
        if (keyValue) {
            key = keyValue = keyValue.replace(/\+/g, '%20');
            splitPoint = keyValue.indexOf('=');
            if (splitPoint !== -1) {
                key = keyValue.substring(0, splitPoint);
                val = keyValue.substring(splitPoint + 1);
            }
            key = tryDecodeURIComponent(key);
            if (isDefined(key)) {
                val = isDefined(val) ? tryDecodeURIComponent(val) : true;
                if (!hasOwnProperty.call(obj, key)) {
                    obj[key] = val;
                } else if (isArray(obj[key])) {
                    obj[key].push(val);
                } else {
                    obj[key] = [obj[key], val];
                }
            }
        }
    });
    return obj;
}



/**
 * We need our custom method because encodeURIComponent is too aggressive and doesn't follow
 * http://www.ietf.org/rfc/rfc3986.txt with regards to the character set (pchar) allowed in path
 * segments:
 *    segment       = *pchar
 *    pchar         = unreserved / pct-encoded / sub-delims / ":" / "@"
 *    pct-encoded   = "%" HEXDIG HEXDIG
 *    unreserved    = ALPHA / DIGIT / "-" / "." / "_" / "~"
 *    sub-delims    = "!" / "$" / "&" / "'" / "(" / ")"
 *                     / "*" / "+" / "," / ";" / "="
 */
export const encodeUriSegment = function encodeUriSegment(val) {
    return encodeUriQuery(val, true)
        .replace(/%26/gi, '&')
        .replace(/%3D/gi, '=')
        .replace(/%2B/gi, '+');
}

/**
 * This method is intended for encoding *key* or *value* parts of query component. We need a custom
 * method because encodeURIComponent is too aggressive and encodes stuff that doesn't have to be
 * encoded per http://tools.ietf.org/html/rfc3986:
 *    query         = *( pchar / "/" / "?" )
 *    pchar         = unreserved / pct-encoded / sub-delims / ":" / "@"
 *    unreserved    = ALPHA / DIGIT / "-" / "." / "_" / "~"
 *    pct-encoded   = "%" HEXDIG HEXDIG
 *    sub-delims    = "!" / "$" / "&" / "'" / "(" / ")"
 *                     / "*" / "+" / "," / ";" / "="
 */
export const encodeUriQuery = function encodeUriQuery(val, pctEncodeSpaces) {
    return encodeURIComponent(val)
        .replace(/%40/gi, '@')
        .replace(/%3A/gi, ':')
        .replace(/%24/g, '$')
        .replace(/%2C/gi, ',')
        .replace(/%3B/gi, ';')
        .replace(/%20/g, (pctEncodeSpaces ? '%20' : '+'));
};


export const hasOwnProperty = Object.prototype.hasOwnProperty;

/**
 *
 * @description Converts the specified string to lowercase.
 * @param {string} string String to be converted to lowercase.
 * @returns {string} Lowercased string.
 */
export const lowercase = function(string) { return isString(string) ? string.toLowerCase() : string; };

/**
 *
 * @description Converts the specified string to uppercase.
 * @param {string} string String to be converted to uppercase.
 * @returns {string} Uppercased string.
 */
export const uppercase = function(string) { return isString(string) ? string.toUpperCase() : string; };


//var manualLowercase = function(s) {
/* eslint-disable no-bitwise */
//    return isString(s) ?
//        s.replace(/[A-Z]/g, function(ch) { return String.fromCharCode(ch.charCodeAt(0) | 32); }) :
//        s;
/* eslint-enable */
//};
//var manualUppercase = function(s) {
/* eslint-disable no-bitwise */
//    return isString(s) ?
//        s.replace(/[a-z]/g, function(ch) { return String.fromCharCode(ch.charCodeAt(0) & ~32); }) :
//        s;
/* eslint-enable */
//};


// String#toLowerCase and String#toUpperCase don't produce correct results in browsers with Turkish
// locale, for this reason we need to detect this case and redefine lowercase/uppercase methods
// with correct but slower alternatives. See https://github.com/angular/angular.js/issues/11387
/*if ('i' !== 'I'.toLowerCase()) {
    lowercase = manualLowercase;
    uppercase = manualUppercase;
}*/



/*
 * @description
 * Determines if a reference is a `Number`.
 *
 * This includes the "special" numbers `NaN`, `+Infinity` and `-Infinity`.
 *
 * If you wish to exclude these then you can use the native
 * [`isFinite'](https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/isFinite)
 * method.
 *
 * @param {*} value Reference to check.
 * @returns {boolean} True if `value` is a `Number`.
 */
export const isNumber = function isNumber(value) { return typeof value === 'number'; }


/*
 * @description
 * Determines if a reference is a `String`.
 *
 * @param {*} value Reference to check.
 * @returns {boolean} True if `value` is a `String`.
 */
export const isString = (value) => { return typeof value === 'string'; }

/* @kind function
 *
 * @description
 * Determines if a value is a date.
 *
 * @param {*} value Reference to check.
 * @returns {boolean} True if `value` is a `Date`.
 */
export const isDate = (value) => {
    return toString.call(value) === '[object Date]';
};


// eslint-disable-next-line no-self-compare
export const simpleCompare = (a, b) => { return a === b || (a !== a && b !== b); }

/**
 * Determines if a value is a regular expression object.
 *
 * @private
 * @param {*} value Reference to check.
 * @returns {boolean} True if `value` is a `RegExp`.
 */
export const isRegExp = (value) => {
    return toString.call(value) === '[object RegExp]';
}

/*
 *
 * @description
 * Determines if a reference is a `Function`.
 *
 * @param {*} value Reference to check.
 * @returns {boolean} True if `value` is a `Function`.
 */
export const isFunction = (value) => { return typeof value === 'function'; }


export const toInt = (str) => {
    return parseInt(str, 10);
}

export const isNumberNaN = Number.isNaN || function isNumberNaN(num) {
    // eslint-disable-next-line no-self-compare
    return num !== num;
};

/*
* @decription
* ${key} is a period delimited string used to deep access ${obj}
*
* @param {object literal} obj
* @param {string} key
* @returns {*} value of key specified in ${key}
* @example
* ```js
* let customerData = {
*    user:{
*        mainAddress:{
*            street1:"1234 Hickory St",
*            street2:"Apt 2"
*            city:"Orem",
*            state:"Utah"
*        }
*    }
*
* };
* let stringToDeepAccess = 'user.mainAddress.city';
* let val = deepAccess(customerData,stringToDeepAccess);
* //${val} equals "Orem"
*
* ```
*
*/

export const deepAccess = (obj, key) => {
    return key.split('.').reduce((nestedObject, key) => {
        if (nestedObject && key in nestedObject) {
            return nestedObject[key];
        }
        return undefined;
    }, obj);
};

/*
* @kind function
* @description
* Sorts an array of objects by the key-value specified.
* sortBy is a string and may represent a child deeply nested in the object.
*
* @param {array} arr is an array containing object literals of indetermined depth
* @param {string} sortBy points at the key to access within the object. sortBy may represent value deep within object
* @returns {array} of sorted items based on the key-value specified
* @example
* ```js
* let arr = [
*   {user:{address:{city:"Orem"}}}
*   {user:{address:{city:"Seattle"}}}
*   {user:{address:{city:"Tacoma"}}}
*   {user:{address:{city:"Salt Lake City"}}}
* ];
* let sortBy = 'user.address.city';
* let sortedArr = deepSort(arr, sortBy);
* //sortedArr sorts based on city name alphabetically
*
* ```
*
*/
export const deepSort = (arr, sortBy) => {

    if (!isArray(arr)) {
        return false;
    }

    return arr.sort(function(a, b) {
        let aVal = deepAccess(a, sortBy);
        let bVal = deepAccess(b, sortBy);
        return aVal.localeCompare(bVal);
    });
};

export const getMedium = () => {
   try {
      if(/Android|webOS|iPhone|iPad|iPod|pocket|psp|kindle|avantgo|blazer|midori|Tablet|Palm|maemo|plucker|phone|BlackBerry|symbian|IEMobile|mobile|ZuneWP7|Windows Phone|Opera Mini/i.test(navigator.userAgent)) {
       return "Mobile";
      }
      return "Internet";
   } catch(e){ console.log("Error in isMobile"); return false; }
 };

export const getOS = () => {
   var OSName="Unknown OS";
   if (navigator.appVersion.indexOf("Win")!==-1) OSName="Windows";
   if (navigator.appVersion.indexOf("Mac")!==-1) OSName="MacOS";
   if (navigator.appVersion.indexOf("X11")!==-1) OSName="UNIX";
   if (navigator.appVersion.indexOf("Linux")!==-1) OSName="Linux";

   // Return the OS name
   return OSName;
}

export const isIe = () => {
      var ua = window.navigator.userAgent;
      return !!ua.match(/MSIE|Trident/ig);
    }

  /**
  * Convert words first character upper case
  *
  * @private
  * @param {*} value Reference to check.
  * @returns {string} new string.
  */
  export const ucFirst = (value) => {
    if(!value) return;
    if(typeof(value) !== 'string') return;
    return value
          .toLowerCase()
          .split(' ')
          .map(word => word.charAt(0).toUpperCase() + word.slice(1))
          .join(' ');
  }

export const isProd = window.location.hostname.includes("unicity.com");


// WEBPACK FOOTER //
// ./src/services/utils/index.js